import React, { useCallback, useState, useRef } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'next-i18next';
import { textP, textSmall } from '@features/ui/styles/textStyles';
import Icon, { ICON_NAME } from '@features/ui/components/Icon';

export interface InputFileUploadProps {
  onChange?: (file: File | null) => void;
  disabled?: boolean;
  acceptedTypes?: string[];
  maxSizeInMB?: number;
  value?: File | null;
  defaultFile?: { name: string; url: string };
  meta?: InputFileUploadMeta;
  onRemove?: () => void;
  isLoading?: boolean;
  uploadProgress?: number;
  canReplace?: boolean;
  // Props filtrées qui ne doivent pas atteindre le DOM
  showRequiredChip?: boolean;
  categoryID?: number;
  required?: boolean;
  label?: string;
}

interface InputFileUploadMeta {
  touched: boolean;
  error: string | boolean;
}

export enum UPLOAD_STATES {
  IDLE = 'idle',
  DRAG_OVER = 'dragOver',
  UPLOADING = 'uploading',
  SUCCESS = 'success',
  ERROR = 'error',
}

const InputFileUpload = ({
  onChange,
  disabled = false,
  acceptedTypes = ['image/jpeg', 'image/png', 'application/pdf'],
  maxSizeInMB = 10,
  value,
  defaultFile,
  meta,
  onRemove,
  isLoading = false,
  uploadProgress = 0,
  canReplace = true,
  // Filtrer les props non-DOM
  showRequiredChip,
  categoryID,
  required,
  label,
  ...props
}: InputFileUploadProps) => {
  const { t } = useTranslation();
  const [uploadState, setUploadState] = useState<UPLOAD_STATES>(UPLOAD_STATES.IDLE);
  const [dragCounter, setDragCounter] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasError = meta && meta.touched && meta.error;
  const hasFile = value || defaultFile;

  const validateFile = (file: File): string | null => {
    if (!acceptedTypes.includes(file.type)) {
      return t('inputFileUpload_invalidType.message', { types: acceptedTypes.map(type => type.split('/')[1]).join(', ') });
    }

    if (file.size > maxSizeInMB * 1024 * 1024) {
      return t('inputFileUpload_tooLarge.message', { size: maxSizeInMB });
    }

    return null;
  };

  const handleFileSelect = (file: File) => {
    const error = validateFile(file);
    if (error) {
      setUploadState(UPLOAD_STATES.ERROR);
      setValidationError(error);
      return;
    }

    setUploadState(UPLOAD_STATES.SUCCESS);
    setValidationError(null);
    onChange?.(file);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setUploadState(UPLOAD_STATES.DRAG_OVER);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev - 1);
    if (dragCounter <= 1) {
      setUploadState(UPLOAD_STATES.IDLE);
    }
  }, [dragCounter]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setUploadState(UPLOAD_STATES.IDLE);
    setDragCounter(0);

    if (disabled) return;

    const { files } = e.dataTransfer;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [disabled]);

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const getDisplayFile = () => {
    if (value) {
      return {
        name: value.name,
        isNew: true,
      };
    }
    if (defaultFile) {
      return {
        name: defaultFile.name,
        isNew: false,
        url: defaultFile.url,
      };
    }
    return null;
  };

  const displayFile = getDisplayFile();

  return (
    <Container>
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleInputChange}
        accept={acceptedTypes.join(',')}
        style={{ display: 'none' }}
        disabled={disabled}
        {...props}
      />

      {!hasFile && (
        <DropZone
          onClick={handleClick}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          uploadState={uploadState}
          disabled={disabled}
          hasError={!!hasError}
        >
          <DropZoneContent>
            {isLoading ? (
              <>
                <UploadIcon>
                  <LoadingSpinner />
                </UploadIcon>
                <DropZoneText>
                  {t('inputFileUpload_uploading.message', 'Upload en cours...')}
                </DropZoneText>
                {uploadProgress > 0 && (
                  <ProgressBar>
                    <ProgressFill progress={uploadProgress} />
                  </ProgressBar>
                )}
                <DropZoneSubText>
                  {uploadProgress > 0 ? `${uploadProgress}%` : ''}
                </DropZoneSubText>
              </>
            ) : (
              <>
                <UploadIcon>
                  <Icon
                    name={ICON_NAME.add}
                    color={disabled ? 'var(--color-grey3)' : 'var(--color-grey4)'}
                  />
                </UploadIcon>
                <DropZoneText>
                  {uploadState === UPLOAD_STATES.DRAG_OVER
                    ? t('inputFileUpload_dropFile.message')
                    : t('inputFileUpload_dragOrClick.message')}
                </DropZoneText>
                <DropZoneSubText>
                  {t('inputFileUpload_acceptedTypes.message', {
                    types: acceptedTypes.map(type => type.split('/')[1].toUpperCase()).join(', '),
                    size: maxSizeInMB,
                  })}
                </DropZoneSubText>
              </>
            )}
          </DropZoneContent>
        </DropZone>
      )}

      {displayFile && (
        <FilePreview hasError={!!hasError}>
          <FileInfo>
            <FileIcon>
              <Icon
                name={ICON_NAME.description}
                color="var(--color-grey4)"
              />
            </FileIcon>
            <FileDetails>
              <FileName>{displayFile.name}</FileName>
              {displayFile.isNew && (
                <FileStatus success={uploadState === UPLOAD_STATES.SUCCESS}>
                  {uploadState === UPLOAD_STATES.SUCCESS
                    ? t('inputFileUpload_uploadSuccess.message')
                    : t('inputFileUpload_uploadReady.message')}
                </FileStatus>
              )}
            </FileDetails>
          </FileInfo>

          <FileActions>
            {!displayFile.isNew && displayFile.url && (
              <FileAction
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(displayFile.url, '_blank');
                }}
                title={t('inputFileUpload_download.message')}
              >
                <Icon name={ICON_NAME.downloadFile} color="var(--color-grey4)" />
              </FileAction>
            )}

            {canReplace && (
              <FileAction
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick();
                }}
                title={t('inputFileUpload_replace.message')}
              >
                <Icon name={ICON_NAME.iconEdit} color="var(--color-grey4)" />
              </FileAction>
            )}
          </FileActions>
        </FilePreview>
      )}

      {/* Affichage des erreurs */}
      {(validationError || (hasError && typeof meta.error === 'string')) && (
        <ErrorMessage>
          {validationError || meta.error}
        </ErrorMessage>
      )}
    </Container>
  );
};

export default InputFileUpload;

const Container = styled.div`
  width: 100%;
`;

const DropZone = styled.div<{
  uploadState: UPLOAD_STATES;
  disabled: boolean;
  hasError: boolean;
}>`
  border: 2px dashed var(--color-grey3);
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  background-color: ${({ disabled }) => (disabled ? 'var(--color-grey1)' : 'var(--color-white)')};
  
  ${({ uploadState }) => uploadState === UPLOAD_STATES.DRAG_OVER && css`
    border-color: var(--color-orange);
    background-color: var(--color-orange-light);
  `}
  
  ${({ hasError }) => hasError && css`
    border-color: var(--color-red);
    border-left: solid 5px var(--color-red);
  `}
  
  &:hover {
    ${({ disabled, hasError }) => !disabled && !hasError && css`
      border-color: var(--color-orange-light);
      background-color: var(--color-grey1);
    `}
  }
`;

const DropZoneContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const UploadIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DropZoneText = styled.div`
  ${textP};
  color: var(--color-grey4);
  font-weight: 500;
`;

const DropZoneSubText = styled.div`
  ${textSmall};
  color: var(--color-grey3);
`;

const FilePreview = styled.div<{ hasError: boolean }>`
  border: 1px solid var(--color-grey2);
  border-radius: 4px;
  padding: 12px 16px;
  background-color: var(--color-white);
  display: flex;
  align-items: center;
  gap: 12px;
  
  ${({ hasError }) => hasError && css`
    border-color: var(--color-red);
    border-left: solid 3px var(--color-red);
  `}
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const FileIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FileDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

const FileName = styled.div`
  ${textP};
  color: var(--color-grey4);
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;
  min-width: 0;
  display: block;
`;

const FileStatus = styled.div<{ success: boolean }>`
  ${textSmall};
  color: ${({ success }) => (success ? 'var(--color-green)' : 'var(--color-grey3)')};
`;

const FileActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const FileAction = styled.button`
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: var(--color-grey2);
  }
`;

const ErrorMessage = styled.div`
  ${textSmall};
  color: var(--color-red);
  margin-top: 8px;
  padding: 8px 12px;
  background-color: rgba(220, 53, 69, 0.1);
  border: 1px solid var(--color-red);
  border-radius: 4px;
  line-height: 1.4;
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid var(--color-grey2);
  border-top: 2px solid var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background-color: var(--color-grey2);
  border-radius: 3px;
  overflow: hidden;
  margin: 8px 0;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background-color: var(--color-primary);
  border-radius: 3px;
  transition: width 0.3s ease;
  width: ${({ progress }) => progress}%;
`;
